// What a recognised word looks like, measured from the scan's pixels
// (tesseract gives text, boxes and baselines, but no font, size or colour:
// spike S5; its letter boxes are too loose to measure from). Pure functions
// on an RGBA raster in the image's own pixels, so they run in the browser
// and in unit tests alike.
import type { Bbox, Line } from 'tesseract.js';

/** RGBA pixels, row by row (as canvas ImageData). */
export interface Raster {
  data: Uint8ClampedArray | Uint8Array;
  width: number;
  height: number;
}

/** How a word is drawn, in image pixels. */
export interface WordLook {
  /** the font size (em height) */
  size: number;
  /** median colour of its ink, 0–255 */
  color: [number, number, number];
  serif: boolean;
  mono: boolean;
  bold: boolean;
  /** baseline at the word's left and right edge (x, y) */
  baseline: [[number, number], [number, number]];
}

/** Ascender (cap) and x-height as shares of the em, per kind (Liberation Sans, Serif and Mono: the common office fonts). */
// Measured on tests/fixtures/ocr/styles.pdf (300 dpi): the fonts' own proportions plus about a pixel of ink spread.
const RATIOS = { sans: { cap: 0.72, x: 0.535 }, serif: { cap: 0.69, x: 0.475 }, mono: { cap: 0.7, x: 0.545 } };
/**
 * Stroke width as a share of the x-height above which a word is bold: on its own, or in a line that is mostly bold.
 * styles.pdf: regular 0.12–0.16, bold 0.17–0.21; the corpus scans: regular 0.11–0.15, short words of straight
 * stems ("Il", "und", "1111") up to 0.17.
 */
const BOLD_STROKE = 0.19;
const BOLD_STROKE_IN_BOLD = 0.16;
/** Serif: the share of a line's stems that end in a foot more than 1.6 times as wide (styles.pdf: sans and mono 0.13–0.26, serif 0.52–0.75). */
const SERIF_FEET = 0.4;
/** Monospaced: how far word widths stray from a fixed pitch per letter (relative to the pitch), at most. */
const MONO_RESIDUAL = 0.12;

const lum = (d: Raster['data'], i: number) => 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
const sorted = (xs: number[]) => [...xs].sort((a, b) => a - b);
const quantile = (xs: number[], q: number) => (xs.length ? sorted(xs)[Math.min(xs.length - 1, Math.floor(q * xs.length))] : NaN);
const median = (xs: number[]) => quantile(xs, 0.5);

/** Otsu's threshold of the grey levels inside `box`: ink is at or below it (in a black-and-white scan, 0). */
function threshold(img: Raster, box: Bbox): number {
  const hist = new Array<number>(256).fill(0);
  let n = 0;
  for (let y = Math.max(0, box.y0); y < Math.min(img.height, box.y1); y++) {
    for (let x = Math.max(0, box.x0); x < Math.min(img.width, box.x1); x++) {
      hist[Math.round(lum(img.data, (y * img.width + x) * 4))]++;
      n++;
    }
  }
  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t];
  let best = 128;
  let bestVar = -1;
  let wB = 0;
  let sumB = 0;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB || wB === n) continue;
    sumB += t * hist[t];
    const v = wB * (n - wB) * (sumB / wB - (sum - sumB) / (n - wB)) ** 2;
    if (v > bestVar) {
      bestVar = v;
      best = t;
    }
  }
  return best;
}

/** The baseline's y at x (the line's baseline runs from (x0, y0) to (x1, y1)). */
function baselineAt(line: Line, x: number): number {
  const b = line.baseline;
  return b.x1 === b.x0 ? b.y0 : b.y0 + ((x - b.x0) * (b.y1 - b.y0)) / (b.x1 - b.x0);
}

/** The raw measures of a line and its words (image pixels), from which lookOfLine decides. */
export interface LineMeasures {
  xHeight: number;
  /** the ascender line (tall letters, capitals, digits) above the baseline */
  capHeight: number;
  /** share of stems that end in a foot more than 1.6 times as wide */
  feet: number;
  /** how far word widths stray from a fixed pitch per letter (relative) */
  monoResidual: number;
  /** where the ink sits on its baseline, relative to tesseract's (pixels, + = lower): the bottom of the letters */
  baselineShift: number;
  words: { stroke: number; color: [number, number, number] }[];
}

export function measureLine(img: Raster, line: Line): LineMeasures {
  const t = threshold(img, line.bbox);
  const ink = (x: number, y: number) => x >= 0 && y >= 0 && x < img.width && y < img.height && lum(img.data, (y * img.width + x) * 4) <= t;
  // Each column's highest ink above the baseline (two pixels of ink in a row, so noise does not count).
  const topsIn = (box: Bbox): number[] => {
    const out: number[] = [];
    for (let x = box.x0; x < box.x1; x++) {
      const base = Math.round(baselineAt(line, x));
      for (let y = Math.min(box.y0, line.bbox.y0); y < base; y++) {
        if (ink(x, y) && ink(x, y + 1)) {
          out.push(base - y);
          break;
        }
      }
    }
    return out;
  };
  // Tall letters reach the top cluster; the x-height is the most common top below it.
  const heightsOf = (tops: number[]) => {
    const hi = quantile(tops, 0.95);
    return { cap: median(tops.filter((d) => d >= 0.87 * hi)), x: median(tops.filter((d) => d > 0.45 * hi && d < 0.82 * hi)) };
  };
  const lineHeights = heightsOf(topsIn(line.bbox));
  const xh = Number.isFinite(lineHeights.x) ? lineHeights.x : 0.7 * lineHeights.cap;
  // Stroke width: twice the ink area over its outline (a stroke of width w and length L has area wL and outline
  // about 2L), between the baseline and the x-height, where every letter has strokes and dots and accents do not.
  const strokeIn = (box: Bbox): number => {
    let area = 0;
    let edges = 0;
    for (let x = box.x0; x < box.x1; x++) {
      const base = Math.round(baselineAt(line, x));
      for (let y = Math.round(base - xh); y < base; y++) {
        if (!ink(x, y)) continue;
        area++;
        edges += Number(!ink(x - 1, y)) + Number(!ink(x + 1, y)) + Number(!ink(x, y - 1)) + Number(!ink(x, y + 1));
      }
    }
    return edges ? (2 * area) / edges : NaN;
  };
  const stroke = strokeIn(line.bbox);
  // Serif feet: where a single stem crosses the middle of the x-height, the ink just above the baseline below it
  // is much wider than the stroke (a sans stem ends as wide as it is; round letters cross the middle twice).
  let stems = 0;
  let footed = 0;
  for (const w of line.words) {
    const mid = (x: number) => Math.round(baselineAt(line, x) - 0.5 * xh);
    // Two rows in the foot (a serif is a thin slab on the baseline), the wider run counts.
    const feetRows = [0.05, 0.12].map((f) => Math.max(1, f * xh));
    for (let x = w.bbox.x0; x < w.bbox.x1; x++) {
      if (!ink(x, mid(x)) || ink(x - 1, mid(x))) continue;
      let end = x;
      while (ink(end + 1, mid(x))) end++;
      const width = end - x + 1;
      if (width <= 2 * stroke) {
        // The run of ink at the foot under this stem, and whether another stem crosses the middle above it.
        const c = Math.round((x + end) / 2);
        let widest: [number, number] | undefined;
        for (const up of feetRows) {
          const y = Math.round(baselineAt(line, c) - up);
          if (!ink(c, y)) continue;
          let a = c;
          let b = c;
          while (ink(a - 1, y)) a--;
          while (ink(b + 1, y)) b++;
          if (!widest || b - a > widest[1] - widest[0]) widest = [a, b];
        }
        if (widest) {
          let crossings = 0;
          for (let k = widest[0]; k <= widest[1]; k++) if (ink(k, mid(k)) && !ink(k - 1, mid(k))) crossings++;
          if (crossings === 1) {
            stems++;
            if (widest[1] - widest[0] + 1 > 1.6 * width) footed++;
          }
        }
      }
      x = end;
    }
  }
  // Monospaced: word width = letters × pitch − a constant (the side bearings), fitted by least squares. Only words
  // with letters, of at least three lengths (digits are equally wide in most fonts).
  const pts = line.words.filter((w) => /\p{L}/u.test(w.text) && w.text.trim().length >= 2).map((w) => [[...w.text.trim()].length, w.bbox.x1 - w.bbox.x0]);
  let monoResidual = Infinity;
  if (new Set(pts.map((p) => p[0])).size >= 3) {
    const n = pts.length;
    const mx = pts.reduce((a, p) => a + p[0], 0) / n;
    const my = pts.reduce((a, p) => a + p[1], 0) / n;
    const sxx = pts.reduce((a, p) => a + (p[0] - mx) ** 2, 0);
    if (sxx > 0) {
      const pitch = pts.reduce((a, p) => a + (p[0] - mx) * (p[1] - my), 0) / sxx;
      const rms = Math.sqrt(pts.reduce((a, p) => a + (p[1] - my - pitch * (p[0] - mx)) ** 2, 0) / n);
      if (pitch > 0) monoResidual = rms / pitch;
    }
  }
  const words = line.words.map((w) => {
    // Colour: the darkest quarter of the ink (its edges are mixed with the paper, and thin strokes have few pixels
    // of full ink).
    const dark: [number, number][] = [];
    for (let y = Math.max(0, w.bbox.y0); y < Math.min(img.height, w.bbox.y1); y++) {
      for (let x = Math.max(0, w.bbox.x0); x < Math.min(img.width, w.bbox.x1); x++) {
        const i = (y * img.width + x) * 4;
        const l = lum(img.data, i);
        if (l <= t) dark.push([l, i]);
      }
    }
    const lq = quantile(dark.map((p) => p[0]), 0.25);
    const core = dark.filter((p) => p[0] <= lq);
    const color = [0, 1, 2].map((c) => Math.round(median(core.map((p) => img.data[p[1] + c])) || 0)) as [number, number, number];
    return { stroke: strokeIn(w.bbox), color };
  });
  // The baseline as the ink shows it: the usual bottom of the letters (columns whose lowest ink is near tesseract's
  // baseline; descenders reach further down and are left out). tesseract's own lies a pixel or two lower.
  const bottoms: number[] = [];
  for (let x = line.bbox.x0; x < line.bbox.x1; x++) {
    const base = baselineAt(line, x);
    for (let y = Math.min(img.height - 1, Math.round(base + 0.25 * xh)); y >= Math.round(base - 0.4 * xh); y--) {
      if (ink(x, y) && ink(x, y - 1)) {
        if (y + 1 - base <= 0.25 * xh) bottoms.push(y + 1 - base);
        break;
      }
    }
  }
  const baselineShift = bottoms.length >= 10 ? median(bottoms) : 0;
  return { xHeight: lineHeights.x, capHeight: lineHeights.cap, feet: stems ? footed / stems : 0, monoResidual, baselineShift, words };
}

/**
 * How each word of a recognised line is drawn. The kind (serif, mono) is decided for the whole line, which is
 * mostly in one face and gives the measures more letters; weight, colour and size are each word's own.
 */
export function lookOfLine(img: Raster, line: Line): WordLook[] {
  const m = measureLine(img, line);
  const mono = m.monoResidual <= MONO_RESIDUAL;
  const serif = !mono && m.feet >= SERIF_FEET;
  const kind = mono ? RATIOS.mono : serif ? RATIOS.serif : RATIOS.sans;
  // The size is the line's: a single word often lacks tall or short letters to measure (tesseract starts a new
  // line where the size changes).
  const sizes = [m.capHeight / kind.cap, m.xHeight / kind.x].filter(Number.isFinite);
  const size = sizes.length ? sizes.reduce((a, b) => a + b, 0) / sizes.length : (line.bbox.y1 - line.bbox.y0) / 1.2;
  const xh = Number.isFinite(m.xHeight) ? m.xHeight : kind.x * size;
  const ratio = m.words.map((w) => w.stroke / xh);
  // A word of one letter has too little ink to tell: it goes with most of its line.
  const letters = (w: Line['words'][number]) => [...w.text].filter((c) => /\p{L}|\p{N}/u.test(c)).length;
  const long = line.words.map((w) => letters(w) >= 2);
  const lineBold = ratio.filter((r, k) => r > BOLD_STROKE_IN_BOLD && long[k]).length > long.filter(Boolean).length / 2;
  const heavy = ratio.map((r) => r > (lineBold ? BOLD_STROKE_IN_BOLD : BOLD_STROKE));
  return line.words.map((w, k) => {
    const own = m.words[k];
    return {
      size,
      color: own.color,
      serif,
      mono,
      bold: long[k] ? heavy[k] : lineBold,
      baseline: [
        [w.bbox.x0, baselineAt(line, w.bbox.x0) + m.baselineShift],
        [w.bbox.x1, baselineAt(line, w.bbox.x1) + m.baselineShift],
      ],
    };
  });
}
